"use client"

import { useState, useEffect } from "react"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import EditProfileModal from "../../components/modals/EditProfileModal"
import LocationChangeModal from "../../components/modals/LocationChangeModal"
import LocationSuccessModal from "../../components/modals/LocationSuccessModal"
import ChangePasswordModal from "../../components/modals/ChangePasswordModal"
import Navbar from "../../components/account/Navbar"
import { NotificationSettingsContent } from "./NotificationSettings"
import { AccountManagementContent } from "./AccountManagement"
import { FinancialManagementContent } from "./FinancialManagement"
import { PrivacySettingsContent } from "./PrivacySettings"
import ReferralCodeSection from "./ReferralCodeSection"
import { useAuth } from "../../hooks/useAuth"
import { useTranslation } from "react-i18next"
import { resolveImageUrl, getUserPlaceholderImage } from "../../utils/imageUtils"
import "./accountmanage.css"


// Main Account Preferences Component
const AccountPreferences = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { userType, tab } = useParams()
  const { getUserType } = useAuth()
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir() === "rtl"

  // Get user type color based on user type
  const getUserTypeColor = () => {
    const currentUserType = getUserType() || userType
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8', // Purple
      'VETERINARIAN': '#00D4B3', // Cyan
      'SERVICE_PROVIDER': '#D65745', // Orange
      'CLINIC_MODERATOR': '#2A8AD2', // Blue
      'FIRM': '#1B3A6B', // Navy Blue
    }
    return userTypeConfig[currentUserType] || '#8548A8' // Default to purple
  }

  const userTypeColor = getUserTypeColor()

  // Get the current tab from URL parameter or default to 'profile'
  const getCurrentTabFromUrl = () => {
    if (tab) {
      switch (tab) {
        case "notifications":
          return "notifications"
        case "management":
          return "management"
        case "privacy":
          return "privacy"
        case "referral":
          return "referral"
        case "profile":
        default:
          return "profile"
      }
    }
    return "profile"
  }

  const [activeTab, setActiveTab] = useState(getCurrentTabFromUrl())
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isLocationChangeModalOpen, setIsLocationChangeModalOpen] = useState(false)
  const [isLocationSuccessModalOpen, setIsLocationSuccessModalOpen] = useState(false)
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false)
  const [oldLocation, setOldLocation] = useState("")
  const [newLocation, setNewLocation] = useState("")
  const [originalCountry, setOriginalCountry] = useState('')
  const [countryChanged, setCountryChanged] = useState(false)

  // User data state
  const [userData, setUserData] = useState({
    name: "",
    firstName: "",
    lastName: "",
    username: "",
    dateOfBirth: "",
    gender: "",
    email: "",
    phone: "",
    location: "",
    country: "",
    city: "",
    countryId: "",
    cityId: "",
    profileImage: null,
    // Firm specific fields
    nameEn: "",
    nameAr: "",
    legalNumber: "",
    address: "",
    contactInfo: "",
    userType: "",
  })

  const [isLoading, setIsLoading] = useState(true)
  const [successMessageKey, setSuccessMessageKey] = useState(null)
  const [errorMessageKey, setErrorMessageKey] = useState(null)

  const formatDateValue = (value) => {
    if (!value) return ""
    // If value is already in YYYY-MM-DD format, use it directly
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const parsed = new Date(value + 'T00:00:00') // Add time to avoid timezone issues
      if (Number.isNaN(parsed.getTime())) {
        return value
      }
      try {
        return new Intl.DateTimeFormat(i18n.language || undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        }).format(parsed)
      } catch {
        return value
      }
    }
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) {
      return value
    }
    try {
      return new Intl.DateTimeFormat(i18n.language || undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      }).format(parsed)
    } catch {
      // Format date in local timezone to avoid timezone conversion issues
      const year = parsed.getFullYear()
      const month = String(parsed.getMonth() + 1).padStart(2, '0')
      const day = String(parsed.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }

  const getFieldValue = (value) => value || t("accountPreferences.profile.notProvided")
  const getGenderValue = (value) => {
    if (!value) {
      return t("accountPreferences.profile.notProvided")
    }
    const normalized = value.toLowerCase().replace(/\s+/g, "_")
    const translationKey = `accountPreferences.profile.gender.${normalized}`
    const translated = t(translationKey)
    return translated === translationKey ? value : translated
  }
  const isFirm = (getUserType() || userData.userType) === 'FIRM'

  const profileRows = isFirm ? [
    {
      key: "nameEn",
      label: t("accountPreferences.profile.fields.nameEn"),
      value: getFieldValue(userData.nameEn),
    },
    {
      key: "nameAr",
      label: t("accountPreferences.profile.fields.nameAr"),
      value: getFieldValue(userData.nameAr),
    },
    {
      key: "legalNumber",
      label: t("accountPreferences.profile.fields.legalNumber"),
      value: getFieldValue(userData.legalNumber),
    },
    {
      key: "email",
      label: t("accountPreferences.profile.fields.email"),
      value: getFieldValue(userData.email),
    },
    {
      key: "contactInfo",
      label: t("accountPreferences.profile.fields.contactInfo"),
      value: getFieldValue(userData.contactInfo || userData.phone),
    },
    {
      key: "address",
      label: t("accountPreferences.profile.fields.address"),
      value: getFieldValue(userData.address),
    },
    {
      key: "location",
      label: t("accountPreferences.profile.fields.location"),
      value: getFieldValue(userData.location),
    },
  ] : [
    {
      key: "dateOfBirth",
      label: t("accountPreferences.profile.fields.dateOfBirth"),
      value: userData.dateOfBirth
        ? formatDateValue(userData.dateOfBirth)
        : t("accountPreferences.profile.notProvided"),
    },
    {
      key: "gender",
      label: t("accountPreferences.profile.fields.gender"),
      value: getGenderValue(userData.gender),
    },
    {
      key: "email",
      label: t("accountPreferences.profile.fields.email"),
      value: getFieldValue(userData.email),
    },
    {
      key: "phone",
      label: t("accountPreferences.profile.fields.phone"),
      value: getFieldValue(userData.phone),
    },
    {
      key: "location",
      label: t("accountPreferences.profile.fields.location"),
      value: getFieldValue(userData.location),
    },
  ]
  const displayName = userData.name || t("accountPreferences.profile.defaultName")
  const currentYear = new Date().getFullYear()

  // Sync active tab with URL changes
  useEffect(() => {
    const currentTab = getCurrentTabFromUrl()
    setActiveTab(currentTab)
  }, [location.pathname])

  // Fetch user profile data on component mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        // Check if user is authenticated
        const token = localStorage.getItem('access_token')

        if (!token) {
          setErrorMessageKey("accountPreferences.errors.authRequired")
          setIsLoading(false)
          return
        }

        setIsLoading(true)
        setErrorMessageKey(null)

        // Use the same approach as CompleteProfile.jsx
        const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"

        const response = await fetch(`${API_BASE_URL}/api/v1/user-profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        })

        if (response.ok) {
          const data = await response.json()

          // Handle Laravel backend response structure
          let profileData = null
          if (data.status === 'success' && data.data) {
            profileData = data.data // Laravel success response with data wrapper
          } else if (data.user) {
            profileData = data.user
          } else if (data.id || data.user_id) {
            profileData = data
          }

          if (profileData) {
            // Transform the API response to match our component's expected format
            const transformedData = {
              name: profileData.user_type === 'FIRM' ? (profileData.display_name || profileData.name_en || t("accountPreferences.profile.defaultName")) : (profileData.full_name || profileData.display_name || `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim()),
              firstName: profileData.first_name || "",
              lastName: profileData.last_name || "",
              username: profileData.username || "",
              dateOfBirth: profileData.date_of_birth ? (() => {
                // If already in YYYY-MM-DD format, use it directly
                if (typeof profileData.date_of_birth === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(profileData.date_of_birth)) {
                  return profileData.date_of_birth
                }
                // Otherwise, format date in local timezone to avoid timezone conversion issues
                const date = new Date(profileData.date_of_birth)
                const year = date.getFullYear()
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const day = String(date.getDate()).padStart(2, '0')
                return `${year}-${month}-${day}`
              })() : "",
              gender: profileData.gender ? profileData.gender.charAt(0).toUpperCase() + profileData.gender.slice(1) : "",
              email: profileData.email || "",
              phone: profileData.phone || "",
              location: profileData.full_address || profileData.city || profileData.country || "",
              profileImage: resolveImageUrl(profileData.profile_image_url),
              country: profileData.country,
               city: profileData.city,
              countryId: profileData.country_id,
              cityId: profileData.city_id,
              // Firm specific fields
              nameEn: profileData.name_en || "",
              nameAr: profileData.name_ar || "",
              legalNumber: profileData.legal_number || "",
              address: profileData.address || "",
              contactInfo: profileData.contact_info || "",
              userType: profileData.user_type || "",
            }

            setUserData(transformedData)

            // Store original country for comparison
            setOriginalCountry(transformedData.country || '')
            setCountryChanged(false)
          } else {
            throw new Error("Invalid profile data structure")
          }
        } else {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }
      } catch (error) {
        setErrorMessageKey("accountPreferences.errors.loadProfile")
      } finally {
        setIsLoading(false)
      }
    }

    fetchUserProfile()
  }, [])

  // Tab configuration with icons
  const tabs = [
    {
      key: "profile",
      labelKey: "accountPreferences.tabs.profile",
      path: `/account/preferences/profile`,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
    },
    {
      key: "notifications",
      labelKey: "accountPreferences.tabs.notifications",
      path: `/account/preferences/notifications`,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      ),
    },
    {
      key: "management",
      labelKey: "accountPreferences.tabs.management",
      path: `/account/preferences/management`,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L2 7l10 5 10-5-10-5z" />
          <path d="M2 17l10 5 10-5" />
          <path d="M2 12l10 5 10-5" />
        </svg>
      ),
    },
    {
      key: "privacy",
      labelKey: "accountPreferences.tabs.privacy",
      path: `/account/preferences/privacy`,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <circle cx="12" cy="16" r="1" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      ),
    },
    {
      key: "referral",
      labelKey: "accountPreferences.tabs.referral",
      path: `/account/preferences/referral`,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7" />
          <path d="M2 7h20v5H2z" />
          <path d="M12 22V7" />
          <path d="M12 7H7.5a2.5 2.5 0 1 1 0-5C11 2 12 7 12 7z" />
          <path d="M12 7h4.5a2.5 2.5 0 1 0 0-5C13 2 12 7 12 7z" />
        </svg>
      ),
    },
  ]

  const handleTabClick = (tabConfig) => {
    if (tabConfig.path) {
      navigate(tabConfig.path)
    }
    setActiveTab(tabConfig.key)
  }

  const handleSaveUserData = async (newUserData) => {
    try {

      // Check if country changed specifically
      if (newUserData.country !== originalCountry) {
        setCountryChanged(true)
      }
      // Get the access token
      const token = localStorage.getItem('access_token')
      if (!token) {
        setErrorMessageKey("accountPreferences.errors.authRequired")
        return
      }

      // Transform the data to match the API format
      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"
      // Prepare the data for the API
      // Clean phone number - remove any formatting and ensure it's in the correct format
      let cleanedPhone = newUserData.phone || ''
      if (cleanedPhone) {
        // Remove any parentheses, dashes, or spaces that might be in the phone number
        // Keep the country code format: +966 54564815
        cleanedPhone = cleanedPhone.trim()
      }

      const updateData = {
        first_name: newUserData.firstName,
        last_name: newUserData.lastName,
        email: newUserData.email,
        phone: cleanedPhone,
        gender: newUserData.gender ? newUserData.gender.toLowerCase().replace(/\s+/g, '_') : '',
        country_id: newUserData.countryId,
        city_id: newUserData.cityId,
        dateOfBirth: newUserData.dateOfBirth,
        date_of_birth: newUserData.dateOfBirth,
        // Firm specific fields
        name_en: newUserData.nameEn,
        name_ar: newUserData.nameAr,
        legal_number: newUserData.legalNumber,
        address: newUserData.address,
        contact_info: newUserData.contactInfo,
      }


      const response = await fetch(`${API_BASE_URL}/api/v1/user-profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(updateData)
      })

      if (response.ok) {
        const data = await response.json()

        // Use the data from the API response if available, otherwise use newUserData
        const updatedUserData = data.data || data.user || newUserData

        // Construct derived fields
        const fullName = `${newUserData.firstName || ''} ${newUserData.lastName || ''}`.trim()

        let newLocation = userData.location
        if (newUserData.country || newUserData.city) {
          const countryName = newUserData.country || userData.country || ''
          const cityName = newUserData.city || userData.city || ''
          // Construct location string similar to how backend would
          // If we have both, usually "City, Country"
          if (cityName && countryName) {
            newLocation = `${cityName}, ${countryName}`
          } else {
            newLocation = cityName || countryName || ""
          }
        }

        // Update the local state with the new data, ensuring derived fields are updated
        setUserData(prev => ({
          ...prev,
          ...updatedUserData, // Basic fields from backend
          firstName: newUserData.firstName,
          lastName: newUserData.lastName,
          name: fullName || prev.name,
          countryId: newUserData.countryId,
          cityId: newUserData.cityId,
          country: newUserData.country,
          city: newUserData.city,
          location: newLocation,
          phone: newUserData.phone,
          email: newUserData.email,
          dateOfBirth: newUserData.dateOfBirth,
          gender: newUserData.gender,
          nameEn: newUserData.nameEn,
          nameAr: newUserData.nameAr,
          legalNumber: newUserData.legalNumber,
          address: newUserData.address,
          contactInfo: newUserData.contactInfo,
          profileImage: newUserData.profileImage
        }))

        // Dispatch event to notify other components (like UserProfile) that profile was updated
        window.dispatchEvent(new CustomEvent('profileUpdated'))

        // Show success message
        setSuccessMessageKey("accountPreferences.success.profileUpdated")

        // Show location success modal only if country changed
        if (countryChanged) {
          // For display purposes, we need to get the actual country and city names
          // Since we're now using IDs, we'll construct a simple location string
          // The actual names will be fetched by the backend
          // const newLocationValue = "Location updated successfully"
          // setNewLocation(newLocationValue)
          // setIsLocationSuccessModalOpen(true)
        }

        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessageKey(null)
        }, 3000)

      } else {
        await response.json()
        setErrorMessageKey("accountPreferences.errors.updateFailed")
      }

    } catch (error) {
      setErrorMessageKey("accountPreferences.errors.updateFailed")
    }
  }

  const handleCancel = () => {
    navigate(`/pets`)
  }

  // Handle location change from the main page dropdown
  const handleLocationChange = (e) => {
    const selectedLocation = e.target.value
    if (selectedLocation && selectedLocation !== userData.location) {
      setOldLocation(userData.location)
      setNewLocation(selectedLocation)
      setIsLocationChangeModalOpen(true)
    }
  }

  const handleLocationChangeConfirm = async () => {
    // Update the location in user data
    setUserData((prev) => ({
      ...prev,
      location: newLocation,
    }))

    // Close location change modal and show success modal
    setIsLocationChangeModalOpen(false)

    // Simulate API call delay
    await new Promise((resolve) => setTimeout(resolve, 1000))

    setIsLocationSuccessModalOpen(true)
  }

  const handleLocationChangeKeepCurrent = () => {
    // Reset to original location
    setNewLocation("")
    setOldLocation("")
  }

  const handleLocationSuccessContinue = () => {
    // User confirmed the location change, continue with normal flow
  }

  const handlePasswordChangeSuccess = (messageKey) => {
    setSuccessMessageKey(messageKey)
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSuccessMessageKey(null)
    }, 3000)
  }


  return (
    <>
      <div
        className={`accountpreferences-container accountpreferences-container-responsive ${isRTL ? 'rtl' : ''}`}
        style={{ '--user-type-color': userTypeColor }}
        dir={i18n.dir()}
        lang={i18n.language}
      >
        <Navbar userType={userType} />

        <main className="accountpreferences-main accountpreferences-main-responsive">
          {/* Page Header */}
          <div className="accountpreferences-page-header accountpreferences-page-header-responsive">
            <h1 className="accountpreferences-page-title accountpreferences-page-title-responsive">
              {t("accountPreferences.page.title")}
            </h1>
          </div>

          {/* Tabs Navigation */}
          <div className="accountpreferences-tabs-container accountpreferences-tabs-container-responsive">
            <div className="accountpreferences-tabs-inner">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => handleTabClick(tab)}
                  className={`accountpreferences-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
                  dir={i18n.dir()}
                >
                  <span className="accountpreferences-tab-icon">
                    {tab.icon}
                  </span>
                  <span className="accountpreferences-tab-text">
                    {t(tab.labelKey)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Active Tab Content */}
          {activeTab === "profile" && (
            <section className="accountpreferences-tab-content">
              {/* Profile Section */}
              <div className="accountpreferences-profile-section">
                {/* Profile Header */}
                <div className="accountpreferences-profile-header">
                  <div className="accountpreferences-profile-info">
                    {/* Profile Image */}
                    <div className="accountpreferences-profile-avatar">
                      <div className="accountpreferences-profile-avatar-content">
                        <img
                          src={userData.profileImage || getUserPlaceholderImage(userData)}
                          alt={t("accountPreferences.profile.avatarAlt", { name: displayName })}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: "50%"
                          }}
                          onError={(e) => {
                            e.target.src = getUserPlaceholderImage(userData);
                          }}
                        />
                      </div>
                    </div>

                    {/* User Name */}
                    <h2 className="accountpreferences-profile-name">
                      {displayName}
                    </h2>
                  </div>

                  {/* Action Buttons */}
                  <div className="accountpreferences-action-buttons">
                    {/* Edit Profile Button */}
                    <button
                      onClick={() => setIsEditModalOpen(true)}
                      className="accountpreferences-edit-btn"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#6B7280"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 20h9" />
                        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                      </svg>
                    </button>

                    {/* Change Password Button */}
                    <button
                      onClick={() => setIsChangePasswordModalOpen(true)}
                      className="accountpreferences-change-password-btn"
                    >
                      {t("accountPreferences.profile.changePassword")}
                    </button>
                  </div>
                </div>

                {/* Profile Form - Read Only Display */}
                <div className="accountpreferences-profile-form">
                  {profileRows.map((field) => (
                    <div className="accountpreferences-form-row" key={field.key}>
                      <div className="accountpreferences-form-label">
                        <span className="accountpreferences-form-label-text">
                          {field.label}
                        </span>
                      </div>
                      <div className="accountpreferences-form-value">
                        <span className="accountpreferences-form-value-text">
                          {field.value}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>            </section>
          )}
          {activeTab === "notifications" && (
            <div className="accountpreferences-tab-content">
              <NotificationSettingsContent />
            </div>
          )}
          {activeTab === "management" && (
            <div className="accountpreferences-tab-content">
              <AccountManagementContent />
            </div>
          )}
          {/* {activeTab === "financial" && (
            <div className="accountpreferences-tab-content">
              <FinancialManagementContent />
            </div>
          )} */}
          {activeTab === "privacy" && (
            <div className="accountpreferences-tab-content">
              <PrivacySettingsContent />
            </div>
          )}
          {activeTab === "referral" && (
            <div className="accountpreferences-tab-content">
              <ReferralCodeSection />
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="accountpreferences-loading-container">
              <div className="accountpreferences-loading-content">
                <div className="accountpreferences-loading-spinner" />
                <p className="accountpreferences-loading-text">
                  {t("accountPreferences.loading.profile")}
                </p>
              </div>
            </div>
          )}

          {/* Success State */}
          {successMessageKey && (
            <div className="accountpreferences-success-message">
              <div className="accountpreferences-success-content">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#16A34A">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
                <p className="accountpreferences-success-text">
                  {t(successMessageKey)}
                </p>
              </div>
            </div>
          )}

          {/* Error State */}
          {errorMessageKey && !isLoading && (
            <div className="accountpreferences-error-message">
              <div className="accountpreferences-error-content">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#DC2626">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
                <p className="accountpreferences-error-text">
                  {t(errorMessageKey)}
                </p>
              </div>
              <button
                onClick={() => window.location.reload()}
                className="accountpreferences-error-btn"
              >
                {t("accountPreferences.actions.retry")}
              </button>
            </div>
          )}




        </main>

        <footer className="accountpreferences-footer">
          <div className="accountpreferences-footer-content">
            <div className="accountpreferences-footer-text">
              {t("accountPreferences.footer.prefix")}{" "}
              <span className="accountpreferences-footer-company">Rubix</span>{" "}
              {t("accountPreferences.footer.suffix", { year: currentYear })}
            </div>
          </div>
        </footer>

        {/* Edit Profile Modal */}
        <EditProfileModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          userData={userData}
          onSave={handleSaveUserData}
        />
      </div>

      {/* Location Change Modal */}
      <LocationChangeModal
        isOpen={isLocationChangeModalOpen}
        onClose={() => setIsLocationChangeModalOpen(false)}
        oldLocation={oldLocation}
        newLocation={newLocation}
        onConfirm={handleLocationChangeConfirm}
        onKeepCurrent={handleLocationChangeKeepCurrent}
      />

      {/* Location Success Modal */}
      <LocationSuccessModal
        isOpen={isLocationSuccessModalOpen}
        onClose={() => setIsLocationSuccessModalOpen(false)}
        newLocation={newLocation}
        onContinue={handleLocationSuccessContinue}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
        onSuccess={handlePasswordChangeSuccess}
      />
    </>
  )
}

export default AccountPreferences
